const DEFAULT_MARGIN_FLOOR = 0.12;
const DEFAULT_PLATFORM_TAKE_RATE = 0.18;
const DEFAULT_PAYMENT_GATEWAY_RATE = 0.02;
const DEFAULT_FRAUD_RESERVE_RATE = 0.015;

function roundMoney(value) {
  return Math.max(0, Math.round(Number(value || 0)));
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function getTransportTimeBandMultiplier(slotLabel = "") {
  if (!slotLabel) return 1;

  const normalized = String(slotLabel).toLowerCase();

  if (normalized.includes("05:") || normalized.includes("06:")) return 1.18;
  if (normalized.includes("07:")) return 1.08;
  if (normalized.includes("18:") || normalized.includes("19:")) return 1.14;
  if (normalized.includes("20:") || normalized.includes("21:")) return 1.24;

  return 1;
}

export function getTransportWeatherMultiplier(weatherBand = "clear") {
  if (weatherBand === "rain") return 1.12;
  if (weatherBand === "heavy_rain") return 1.22;
  if (weatherBand === "heatwave") return 1.08;
  return 1;
}

export function getTransportScarcityMultiplier(scarcityBand = "normal") {
  if (scarcityBand === "tight") return 1.1;
  if (scarcityBand === "critical") return 1.18;
  if (scarcityBand === "oversupplied") return 0.96;
  return 1;
}

export function calculateDynamicTransportQuote({
  labourCount = 1,
  distanceKm = 0,
  slotLabel = "",
  weatherBand = "clear",
  scarcityBand = "normal",
  accessFrictionBand = "standard",
  routePoolingEligible = false,
  pooledStops = 1,
  baseFee = 40,
  perLabourFee = 55,
  perKmFee = 9,
  targetMarginRate = 0.16,
  estimatedPartnerCost = 0,
}) {
  const normalizedLabourCount = Math.max(1, Number(labourCount || 1));
  const normalizedDistanceKm = Math.max(0, Number(distanceKm || 0));

  const labourComponent = normalizedLabourCount * perLabourFee;
  const zoneDistanceComponent = roundMoney(normalizedDistanceKm * perKmFee);
  const timeMultiplier = getTransportTimeBandMultiplier(slotLabel);
  const weatherMultiplier = getTransportWeatherMultiplier(weatherBand);
  const scarcityMultiplier = getTransportScarcityMultiplier(scarcityBand);

  let accessFrictionMultiplier = 1;
  if (accessFrictionBand === "difficult") accessFrictionMultiplier = 1.08;
  if (accessFrictionBand === "hard") accessFrictionMultiplier = 1.16;

  const rawOperationalPrice =
    (baseFee + labourComponent + zoneDistanceComponent) *
    timeMultiplier *
    weatherMultiplier *
    scarcityMultiplier *
    accessFrictionMultiplier;

  const poolingDiscount =
    routePoolingEligible && pooledStops > 1
      ? Math.min(80, 20 * Math.min(pooledStops - 1, 4))
      : 0;

  const fallbackCostEstimate = roundMoney(
    32 + normalizedLabourCount * 48 + normalizedDistanceKm * 7
  );
  const operationalCost = Math.max(
    roundMoney(estimatedPartnerCost),
    fallbackCostEstimate
  );
  const marginProtectedPrice = Math.ceil(
    operationalCost / Math.max(0.01, 1 - targetMarginRate)
  );

  const quotedPrice = Math.max(
    roundMoney(rawOperationalPrice - poolingDiscount),
    marginProtectedPrice
  );

  return {
    quotedPrice,
    operationalCost,
    expectedMargin: roundMoney(quotedPrice - operationalCost),
    expectedMarginRate:
      quotedPrice > 0
        ? Number(((quotedPrice - operationalCost) / quotedPrice).toFixed(3))
        : 0,
    components: {
      baseFee,
      labourComponent,
      zoneDistanceComponent,
      poolingDiscount,
      timeMultiplier,
      weatherMultiplier,
      scarcityMultiplier,
      accessFrictionMultiplier,
      marginProtectedPrice,
    },
  };
}

export function calculateMbCreditRate({
  labourSubtotal = 0,
  predictedContributionMarginRate = 0,
  customerTrustBand = "standard",
  fraudRiskBand = "low",
  customerLifecycleBand = "repeat",
  categoryMarginBand = "standard",
  peakBand = "normal",
}) {
  let rate = 0.02;
  const subtotal = Number(labourSubtotal || 0);

  if (subtotal >= 2500) rate += 0.005;
  if (predictedContributionMarginRate >= 0.2) rate += 0.005;
  if (customerTrustBand === "high") rate += 0.005;
  if (customerLifecycleBand === "winback") rate += 0.01;
  if (customerLifecycleBand === "new") rate -= 0.005;
  if (categoryMarginBand === "strong") rate += 0.005;
  if (peakBand === "off_peak") rate += 0.005;

  if (fraudRiskBand === "medium") rate -= 0.01;
  if (fraudRiskBand === "high") rate -= 0.02;
  if (customerTrustBand === "guarded") rate -= 0.005;
  if (categoryMarginBand === "thin") rate -= 0.01;
  if (predictedContributionMarginRate < 0.14) rate -= 0.015;

  return clamp(Number(rate.toFixed(3)), 0, 0.05);
}

export function calculateMbCredits({
  labourSubtotal = 0,
  predictedContributionMarginRate = 0,
  customerTrustBand = "standard",
  fraudRiskBand = "low",
  customerLifecycleBand = "repeat",
  categoryMarginBand = "standard",
  peakBand = "normal",
  bookingCap = 250,
}) {
  const rate = calculateMbCreditRate({
    labourSubtotal,
    predictedContributionMarginRate,
    customerTrustBand,
    fraudRiskBand,
    customerLifecycleBand,
    categoryMarginBand,
    peakBand,
  });

  const earnedCredits = Math.min(
    roundMoney(Number(labourSubtotal || 0) * rate),
    bookingCap
  );

  return {
    rate,
    earnedCredits,
  };
}

export function calculateContributionMargin({
  labourSubtotal = 0,
  materialSubtotal = 0,
  transportQuote = 0,
  transportCost = 0,
  serviceFee = 0,
  platformFee = 0,
  cashbackCost = 0,
  paymentGatewayRate = DEFAULT_PAYMENT_GATEWAY_RATE,
  supportCost = 0,
  opsOverhead = 0,
  fraudReserveRate = DEFAULT_FRAUD_RESERVE_RATE,
  tax = 0,
}) {
  const totalCustomerPayable =
    Number(labourSubtotal || 0) +
    Number(materialSubtotal || 0) +
    Number(transportQuote || 0) +
    Number(serviceFee || 0) +
    Number(platformFee || 0) +
    Number(tax || 0);

  const paymentGatewayCost = roundMoney(totalCustomerPayable * paymentGatewayRate);
  const fraudReserve = roundMoney(Number(labourSubtotal || 0) * fraudReserveRate);
  const platformRevenue =
    Number(serviceFee || 0) +
    Number(platformFee || 0) +
    roundMoney(Number(labourSubtotal || 0) * DEFAULT_PLATFORM_TAKE_RATE);
  const transportMargin = roundMoney(
    Number(transportQuote || 0) - Number(transportCost || 0)
  );
  const contributionMargin =
    platformRevenue +
    transportMargin -
    Number(cashbackCost || 0) -
    paymentGatewayCost -
    Number(supportCost || 0) -
    Number(opsOverhead || 0) -
    fraudReserve;

  return {
    totalCustomerPayable,
    platformRevenue,
    transportMargin,
    paymentGatewayCost,
    fraudReserve,
    contributionMargin: roundMoney(contributionMargin),
    contributionMarginRate:
      totalCustomerPayable > 0
        ? Number((contributionMargin / totalCustomerPayable).toFixed(3))
        : 0,
  };
}

export function buildCheckoutIntelligence({
  labourSubtotal = 0,
  materialSubtotal = 0,
  serviceFee = 0,
  platformFee = 0,
  tax = 0,
  labourCount = 1,
  distanceKm = 0,
  slotLabel = "",
  weatherBand = "clear",
  scarcityBand = "normal",
  accessFrictionBand = "standard",
  routePoolingEligible = false,
  pooledStops = 1,
  estimatedPartnerCost = 0,
  supportCost = 25,
  opsOverhead = 25,
  customerTrustBand = "standard",
  fraudRiskBand = "low",
  customerLifecycleBand = "repeat",
  categoryMarginBand = "standard",
  peakBand = "normal",
  marginFloor = DEFAULT_MARGIN_FLOOR,
}) {
  const transport = calculateDynamicTransportQuote({
    labourCount,
    distanceKm,
    slotLabel,
    weatherBand,
    scarcityBand,
    accessFrictionBand,
    routePoolingEligible,
    pooledStops,
    estimatedPartnerCost,
  });

  const preRewardMargin = calculateContributionMargin({
    labourSubtotal,
    materialSubtotal,
    transportQuote: transport.quotedPrice,
    transportCost: transport.operationalCost,
    serviceFee,
    platformFee,
    cashbackCost: 0,
    supportCost,
    opsOverhead,
    tax,
  });

  const mbCredits = calculateMbCredits({
    labourSubtotal,
    predictedContributionMarginRate: preRewardMargin.contributionMarginRate,
    customerTrustBand,
    fraudRiskBand,
    customerLifecycleBand,
    categoryMarginBand,
    peakBand,
  });

  let finalMargin = calculateContributionMargin({
    labourSubtotal,
    materialSubtotal,
    transportQuote: transport.quotedPrice,
    transportCost: transport.operationalCost,
    serviceFee,
    platformFee,
    cashbackCost: mbCredits.earnedCredits,
    supportCost,
    opsOverhead,
    tax,
  });

  let suppressedReward = false;

  if (finalMargin.contributionMarginRate < marginFloor) {
    suppressedReward = true;
    finalMargin = calculateContributionMargin({
      labourSubtotal,
      materialSubtotal,
      transportQuote: transport.quotedPrice,
      transportCost: transport.operationalCost,
      serviceFee,
      platformFee,
      cashbackCost: 0,
      supportCost,
      opsOverhead,
      tax,
    });
  }

  return {
    transport,
    mbCredits: {
      ...mbCredits,
      suppressedReward,
      displayLabel: suppressedReward
        ? "Rewards unavailable on this booking"
        : `Earn up to ${roundMoney(mbCredits.earnedCredits)} MB Credits`,
    },
    margin: finalMargin,
  };
}
